import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useParams, Link } from "react-router-dom"
import Select from 'react-select'

import { APIError, useAuth } from "../../../../app/modules/auth"
import { Account, AvailableFRMLocation, clearPassareBranchRouting, createPassareBranchCustomFolderAndFields, createPassareOrganizationCustomFolderAndFields, getAllFRMLocationsForAccountUniqueIdentifier, getPassareOrganizationBranches, getPassareOrganizationByUniqueIdentifier, getPassareOrganizationWebhooks, getPassareWebhookDetail, PassareBranchPair, PassareBranchRoutingEntry, PassareOrganization, PassareWebhookDetail, PassareWebhookHydrationFilter, PassareWebhookSummary, reprocessPassareWebhook, selectFRMLocationForPassareOrganizationByUniqueIdentifier, setPassareBranchRouting, setPassareUnmappedBranchBehavior, updatePassareOrganization, testPassareConnection } from "../../../core/_requests"
import useAccounts from "../../../hooks/useAccounts"
import PassareOrganizationTagsTab from "./PassareOrganizationTagsTab"
import { DisableLinkButton } from "../../service-locations/detail/ServiceLocationSettings"
import { KTCard, KTCardBody } from "../../../../_metronic/helpers"
import { PageTitle } from "../../../../_metronic/layout/core"

type PassareOrganizationDetailContextProps = {
    passareOrganization: PassareOrganization
}

const PassareOrganizationDetailContext = createContext<PassareOrganizationDetailContextProps>({
    passareOrganization: {} as PassareOrganization,
})

export const usePassareOrganizationDetail = () => {
    return useContext(PassareOrganizationDetailContext)
}


function CreateWebhooksCustomFolderAndFields({
    linkOutToFRMWithURL,
    passareOrganization
}: {
    linkOutToFRMWithURL: string | null,
    passareOrganization: PassareOrganization
}) {
    const [creating, setCreating] = useState(false)
    const [errorCreating, setErrorCreating] = useState<string | null>(null)

    const handleCreateFolderAndFields = useCallback(async (force = false) => {
        try {
            setCreating(true)
            setErrorCreating(null)
            await createPassareOrganizationCustomFolderAndFields({
                passare_organization_unique_id: passareOrganization.unique_identifier,
                force: force
            })
            window.location.reload()
        } catch (exception: any) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'Failed to create custom folder and fields for Passare organization'
            setErrorCreating(errorMessage)
        } finally {
            setCreating(false)
        }
    }, [passareOrganization.unique_identifier])

    if (!passareOrganization.private_data?.frm_linkage_information) {
        return null
    }

    return <div className='mt-5 pt-5 border-top'>
        <h4 className="mb-4">360 Custom Folder and Fields</h4>
        {passareOrganization.private_data && (
            <>
                {!passareOrganization.private_data.custom_folder_and_fields_have_been_created ? (
                    <div>
                        {errorCreating && (
                            <div className="alert alert-danger mb-3">
                                <div className="alert-text">{errorCreating}</div>
                            </div>
                        )}
                        <div className="alert alert-info mb-3">
                            <div className="alert-text">
                                Custom fields need to be created in the 360 to properly track Passare leads.
                            </div>
                        </div>
                        <button 
                            className='btn btn-primary' 
                            onClick={() => handleCreateFolderAndFields()} 
                            disabled={creating}
                        >
                            {creating ? 'Creating...' : 'Create 360 Folder and Fields'}
                        </button>
                    </div>
                ) : (
                    <div>
                        {errorCreating && (
                            <div className="alert alert-danger mb-3">
                                <div className="alert-text">{errorCreating}</div>
                            </div>
                        )}
                        <div className="alert alert-success">
                            <div className="d-flex align-items-center">
                                <i className="bi bi-check-circle-fill fs-2 me-3"></i>
                                <div>
                                    <div className="fw-bold">360 Folder and Fields Created</div>
                                    <a href={`${linkOutToFRMWithURL}/v2/location/${passareOrganization.private_data.frm_linkage_information.frm_location_id}/settings/fields`}
                                       target='_blank'
                                       rel='noreferrer'
                                       className="text-success">
                                        View in 360
                                        <i className="bi bi-box-arrow-up-right ms-2"></i>
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div className="mt-3">
                            <button
                                className='btn btn-sm btn-light-primary'
                                onClick={() => handleCreateFolderAndFields(true)}
                                disabled={creating}
                                title="Recreate the custom folder and fields (useful if field definitions have changed)"
                            >
                                <i className="bi bi-arrow-clockwise me-2"></i>
                                {creating ? 'Recreating...' : 'Recreate Fields'}
                            </button>
                            <div className="text-muted mt-2 small">
                                Use this to update field definitions if they've changed (e.g., Disposition → Client Service Type)
                            </div>
                        </div>
                    </div>
                )}
            </>
        )}
    </div>
}

// Sentinel option values for the per-branch routing select.
const ROUTE_USE_DEFAULT = '__USE_DEFAULT__'
const ROUTE_SUPPRESS = '__SUPPRESS__'
const ROUTE_UNSET = '__UNSET__'

type BranchRoutingMap = Record<string, {
    account_unique_identifier?: string
    frm_location_id?: string | null
    branch_name?: string
    suppressed?: boolean
    use_default?: boolean
}>

// Per-branch routing for shared-token Passare orgs: route each branch to its
// own location, or suppress it. Lives in the General tab beneath the location
// linking it extends. Opt-in — orgs with no branches configured are unaffected.
const BranchRoutingSection = ({
    passareOrganization,
    accounts,
}: {
    passareOrganization: PassareOrganization
    accounts: Account[]
}) => {
    const frmLinkage = passareOrganization.private_data?.frm_linkage_information
    const defaultLocationId = frmLinkage?.frm_location_id

    // Account that owns these branches' 360 locations. Pre-filled from an
    // existing default linkage or a prior branch entry; for a no-default
    // multi-branch org (e.g. Gazaille) the admin picks it here — no single
    // default location is ever required.
    const [selectedAccountId, setSelectedAccountId] = useState<string>(
        frmLinkage?.account_unique_identifier
        || Object.values(passareOrganization.private_data?.branch_linkage_information || {})
            .map(e => e?.account_unique_identifier)
            .find(a => !!a)
        || ''
    )

    const [branches, setBranches] = useState<PassareBranchPair[]>([])
    const [availableLocations, setAvailableLocations] = useState<AvailableFRMLocation[]>([])
    const [routing, setRouting] = useState<BranchRoutingMap>(
        passareOrganization.private_data?.branch_linkage_information || {}
    )
    const [unmappedBehavior, setUnmappedBehavior] = useState<'dont_push' | 'use_default'>(
        passareOrganization.private_data?.unmapped_branch_behavior || 'dont_push'
    )
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [savingBranchUuid, setSavingBranchUuid] = useState<string | null>(null)
    const [savingBehavior, setSavingBehavior] = useState(false)
    // Transient "Saved ✓" flash after an auto-save (a branch_uuid, or '__policy__').
    const [savedFlash, setSavedFlash] = useState<string | null>(null)
    // Per-branch 360 field provisioning (branch_uuid currently creating, if any).
    const [provisioningBranchUuid, setProvisioningBranchUuid] = useState<string | null>(null)

    const loadData = useCallback(async () => {
        if (!selectedAccountId) {
            setLoading(false)
            return
        }
        setLoading(true)
        setError(null)
        try {
            const [branchPairs, locations] = await Promise.all([
                getPassareOrganizationBranches(passareOrganization.unique_identifier),
                getAllFRMLocationsForAccountUniqueIdentifier(selectedAccountId),
            ])
            setBranches(branchPairs)
            setAvailableLocations(locations)
        } catch (exception: any) {
            console.error(exception)
            setError(exception instanceof APIError ? exception.message : 'Failed to load branch routing data')
        } finally {
            setLoading(false)
        }
    }, [selectedAccountId, passareOrganization.unique_identifier])

    useEffect(() => {
        loadData()
    }, [loadData])

    const locationName = useCallback((locationId?: string | null) => {
        if (!locationId) return null
        return availableLocations.find(l => l._id === locationId)?.name || locationId
    }, [availableLocations])

    const selectedValueForBranch = (branchUuid: string): string => {
        const entry = routing[branchUuid]
        if (!entry) return ''
        if (entry.suppressed) return ROUTE_SUPPRESS
        if (entry.use_default) return ROUTE_USE_DEFAULT
        if (entry.frm_location_id) return entry.frm_location_id
        return ''
    }

    // True once the org has at least one explicit branch entry. Until then the
    // resolver sends EVERY branch to the org default (today's behavior) and the
    // unmapped policy is NOT consulted — the labels below must reflect that.
    const optedIntoBranchRouting = Object.keys(routing).length > 0

    const effectiveLabelForBranch = (branchUuid: string): string => {
        const entry = routing[branchUuid]
        if (entry?.suppressed) return 'Not pushing (suppressed)'
        if (entry?.use_default) return `→ default location${defaultLocationId ? ` (${locationName(defaultLocationId)})` : ''}`
        if (entry?.frm_location_id) return `→ ${locationName(entry.frm_location_id)}`
        // No explicit entry for this branch.
        if (!optedIntoBranchRouting) {
            // Org hasn't opted in → the resolver routes ALL branches to the default.
            return defaultLocationId
                ? `→ default location (${locationName(defaultLocationId)}) — all branches`
                : 'Not pushing (no location linked)'
        }
        // Opted in → unmapped branches follow the org-level policy.
        return unmappedBehavior === 'use_default'
            ? '→ default location (unmapped)'
            : 'Not pushing (unmapped — set explicitly)'
    }

    const handleSelectBranchRouting = useCallback(async (branch: PassareBranchPair, value: string) => {
        setSavingBranchUuid(branch.branch_uuid)
        setError(null)

        try {
            if (value === ROUTE_UNSET) {
                // Remove the explicit entry → the branch reverts to the org policy.
                await clearPassareBranchRouting(passareOrganization.unique_identifier, branch.branch_uuid)
                setRouting(prev => {
                    const next = { ...prev }
                    delete next[branch.branch_uuid]
                    return next
                })
            } else {
                let entry: PassareBranchRoutingEntry
                if (value === ROUTE_SUPPRESS) {
                    entry = { branch_uuid: branch.branch_uuid, branch_name: branch.branch_name || undefined, suppressed: true, use_default: false, frm_location_id: null }
                } else if (value === ROUTE_USE_DEFAULT) {
                    entry = { branch_uuid: branch.branch_uuid, branch_name: branch.branch_name || undefined, use_default: true, suppressed: false, frm_location_id: null }
                } else {
                    entry = { branch_uuid: branch.branch_uuid, branch_name: branch.branch_name || undefined, frm_location_id: value, account_unique_identifier: selectedAccountId, suppressed: false, use_default: false }
                }
                await setPassareBranchRouting(passareOrganization.unique_identifier, entry)
                setRouting(prev => ({ ...prev, [branch.branch_uuid]: { ...entry } }))
            }
            setSavedFlash(branch.branch_uuid)
            window.setTimeout(() => setSavedFlash(curr => (curr === branch.branch_uuid ? null : curr)), 2000)
        } catch (exception: any) {
            console.error(exception)
            setError(exception instanceof APIError ? exception.message : 'Failed to save branch routing')
        } finally {
            setSavingBranchUuid(null)
        }
    }, [selectedAccountId, passareOrganization.unique_identifier])

    const handleChangeUnmappedBehavior = useCallback(async (behavior: 'dont_push' | 'use_default') => {
        setSavingBehavior(true)
        setError(null)
        try {
            await setPassareUnmappedBranchBehavior(passareOrganization.unique_identifier, behavior)
            setUnmappedBehavior(behavior)
            setSavedFlash('__policy__')
            window.setTimeout(() => setSavedFlash(curr => (curr === '__policy__' ? null : curr)), 2000)
        } catch (exception: any) {
            console.error(exception)
            setError(exception instanceof APIError ? exception.message : 'Failed to save unmapped-branch behavior')
        } finally {
            setSavingBehavior(false)
        }
    }, [passareOrganization.unique_identifier])

    // ── Per-branch 360 custom-field provisioning ─────────────────────────────
    // Locations recorded as provisioned via the per-branch endpoint, plus the
    // legacy case: the org-level card provisions the DEFAULT location and only
    // sets the org boolean (no per-location list existed before 2026-07).
    // Kept in state (seeded from private_data, like `routing` above) so a
    // successful provisioning updates the row in place — no page reload.
    const [orgFieldsFlag, setOrgFieldsFlag] = useState<boolean>(
        !!passareOrganization.private_data?.custom_folder_and_fields_have_been_created
    )
    const [provisionedLocationIds, setProvisionedLocationIds] = useState<string[]>(
        passareOrganization.private_data?.passare_fields_provisioned_location_ids || []
    )
    const locationFieldsProvisioned = useCallback((locationId?: string | null) => {
        if (!locationId) return false
        if (provisionedLocationIds.includes(locationId)) return true
        return orgFieldsFlag && locationId === defaultLocationId
    }, [provisionedLocationIds, orgFieldsFlag, defaultLocationId])

    // 360 base URL for "View in 360" links, from the account that owns these branches.
    const ghlSiteUrl = useMemo(() => {
        const account = accounts.find(a => a.unique_identifier === selectedAccountId)
        return account?.external_ghl_site_url || null
    }, [accounts, selectedAccountId])

    const handleCreateBranchFields = useCallback(async (branch: PassareBranchPair, force = false) => {
        setProvisioningBranchUuid(branch.branch_uuid)
        setError(null)
        try {
            const result = await createPassareBranchCustomFolderAndFields(passareOrganization.unique_identifier, branch.branch_uuid, force)
            // Update in place (no page reload): record the location the backend
            // provisioned and the org-level flag it just set. A fresh page load
            // re-seeds both from the server.
            const locId = result?.frm_location_id || routing[branch.branch_uuid]?.frm_location_id
            if (locId) {
                setProvisionedLocationIds(prev => (prev.includes(locId) ? prev : [...prev, locId]))
            }
            setOrgFieldsFlag(true)
        } catch (exception: any) {
            console.error(exception)
            setError(exception instanceof APIError ? exception.message : 'Failed to create the 360 folder and fields for this branch')
        } finally {
            setProvisioningBranchUuid(null)
        }
    }, [passareOrganization.unique_identifier, routing])

    // "360 Fields" cell: only branches routed to their OWN explicit location can
    // (and need to) be provisioned here; default-routed branches are covered by
    // the org-level card, and suppressed/unset branches receive no pushes.
    const branchFieldsCell = (branch: PassareBranchPair) => {
        const entry = routing[branch.branch_uuid]
        const explicitLocationId =
            entry && !entry.suppressed && !entry.use_default && entry.frm_location_id
                ? entry.frm_location_id
                : null

        if (!explicitLocationId) {
            return <span className="text-muted fs-8">—</span>
        }

        if (provisioningBranchUuid === branch.branch_uuid) {
            return <span className="text-muted fs-7"><span className="spinner-border spinner-border-sm me-1" role="status"></span>Creating…</span>
        }

        if (locationFieldsProvisioned(explicitLocationId)) {
            return (
                <div className="d-flex align-items-center gap-2">
                    <span className="text-success fs-7"><i className="bi bi-check-circle-fill me-1"></i>Fields created</span>
                    {ghlSiteUrl && (
                        <a
                            href={`${ghlSiteUrl}/v2/location/${explicitLocationId}/settings/fields`}
                            target="_blank"
                            rel="noreferrer"
                            className="fs-8"
                        >
                            View in 360<i className="bi bi-box-arrow-up-right ms-1"></i>
                        </a>
                    )}
                    <button
                        className="btn btn-icon btn-sm btn-light w-25px h-25px"
                        title="Recreate the Passare folder and fields at this location (safe — existing fields are treated as success)"
                        onClick={() => handleCreateBranchFields(branch, true)}
                        disabled={provisioningBranchUuid !== null}
                    >
                        <i className="bi bi-arrow-clockwise fs-7"></i>
                    </button>
                </div>
            )
        }

        return (
            <div>
                <button
                    className="btn btn-sm btn-light-primary"
                    onClick={() => handleCreateBranchFields(branch, false)}
                    disabled={provisioningBranchUuid !== null}
                >
                    Create 360 Fields
                </button>
                {orgFieldsFlag && (
                    <div className="text-warning fs-9 mt-1">
                        Passare fields not yet created at this location — synced leads would be missing them.
                    </div>
                )}
            </div>
        )
    }

    const routeOptions = [
        // Common actions first (top of the list), then the location options.
        { value: ROUTE_UNSET, label: '— Not set (follow policy) —' },
        ...(defaultLocationId
            ? [{ value: ROUTE_USE_DEFAULT, label: `Use default location (${locationName(defaultLocationId)})` }]
            : []),
        { value: ROUTE_SUPPRESS, label: 'Do not push (suppress)' },
        ...availableLocations.map(l => ({ value: l._id, label: l.name })),
    ]

    // Footgun guard: with a default location + "Do not push", any branch left
    // unset is suppressed (not sent to the default). Warn when that applies.
    const unmappedBranchCount = branches.filter(b => {
        const e = routing[b.branch_uuid]
        return !e || (!e.suppressed && !e.use_default && !e.frm_location_id)
    }).length
    const showSuppressionWarning =
        !!defaultLocationId && unmappedBehavior === 'dont_push' && unmappedBranchCount > 0 && branches.length > 0

    return (
        <div>
            {error && (
                <div className="alert alert-danger"><div className="alert-text">{error}</div></div>
            )}

            <div className="text-muted fs-7 mb-4">
                For funeral homes that share one Passare token across multiple branches. Pick the 360 account
                below, then route each branch to its own location (or suppress the ones you don't want). No single
                default location is required — anything you don't set explicitly is suppressed. Branches appear
                once at least one of their leads has hydrated.
            </div>

            <div className="d-flex align-items-center text-primary fs-8 mb-4">
                <i className="bi bi-info-circle-fill me-2"></i>
                Each change below saves automatically the moment you make it — there's no Save button here.
            </div>

            <div className="mb-5">
                <label className="form-label fw-bold">360 account for these branches</label>
                <select
                    className="form-select form-select-solid"
                    value={selectedAccountId}
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                >
                    <option value="">Select an account…</option>
                    {accounts.map(a => (
                        <option key={a.unique_identifier} value={a.unique_identifier}>{a.name}</option>
                    ))}
                </select>
                {selectedAccountId && !defaultLocationId && (
                    <div className="text-muted fs-8 mt-2">
                        No default location is set for this org — each branch is routed individually, and any
                        unmatched branch is suppressed (never sent to a fallback).
                    </div>
                )}
            </div>

            {!selectedAccountId ? (
                <div className="alert alert-info">
                    <div className="alert-text">Select the 360 account these branches belong to, above.</div>
                </div>
            ) : loading ? (
                <div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div>
            ) : (
                <>
                    {showSuppressionWarning && (
                        <div className="alert alert-warning d-flex align-items-start">
                            <i className="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
                            <div>
                                <strong>{unmappedBranchCount} branch{unmappedBranchCount === 1 ? '' : 'es'} not set.</strong>{' '}
                                With the <strong>"Do not push"</strong> policy (below), any branch you don't set explicitly is{' '}
                                <strong>suppressed</strong> — it will <strong>not</strong> reach this org's default location.
                                {!optedIntoBranchRouting && " Right now this org sends every branch to that default, so configuring only some branches here would stop the rest."}{' '}
                                To split safely: set <em>every</em> branch explicitly, or switch the policy to <strong>"Send to default location."</strong>
                            </div>
                        </div>
                    )}
                    {branches.length === 0 ? (
                        <div className="alert alert-info">
                            <div className="alert-text">No branches have been seen yet for this organization.</div>
                        </div>
                    ) : (
                        <div className="table-responsive mb-5">
                            <table className="table table-row-bordered align-middle">
                                <thead>
                                    <tr className="fw-bold text-muted">
                                        <th className="min-w-200px">Branch</th>
                                        <th className="min-w-250px">Routing</th>
                                        <th className="min-w-150px">Effective</th>
                                        <th className="min-w-175px">360 Fields</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {branches.map(branch => (
                                        <tr key={branch.branch_uuid}>
                                            <td>
                                                <div className="fw-bold">{branch.branch_name || '(No branch name)'}</div>
                                                <div className="text-muted fs-8">{branch.branch_uuid}</div>
                                            </td>
                                            <td>
                                                <Select
                                                    isDisabled={savingBranchUuid === branch.branch_uuid}
                                                    options={routeOptions}
                                                    value={routeOptions.find(o => o.value === selectedValueForBranch(branch.branch_uuid)) || null}
                                                    placeholder="- Not set (follows policy) -"
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    menuShouldScrollIntoView={false}
                                                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                    onChange={(option) => {
                                                        if (option) handleSelectBranchRouting(branch, option.value)
                                                    }}
                                                    filterOption={(option, searchValue) => {
                                                        const words = searchValue.toLowerCase().split(' ')
                                                        return words.every(word => option.label.toLowerCase().includes(word))
                                                    }}
                                                />
                                            </td>
                                            <td>
                                                {savingBranchUuid === branch.branch_uuid ? (
                                                    <span className="text-muted fs-7"><span className="spinner-border spinner-border-sm me-1" role="status"></span>Saving…</span>
                                                ) : savedFlash === branch.branch_uuid ? (
                                                    <span className="text-success fs-7"><i className="bi bi-check-circle-fill me-1"></i>Saved</span>
                                                ) : (
                                                    <span className="text-muted fs-7">{effectiveLabelForBranch(branch.branch_uuid)}</span>
                                                )}
                                            </td>
                                            <td>{branchFieldsCell(branch)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="separator my-4"></div>

                    <div className="mb-2 fw-bold">
                        Branches you haven't set explicitly
                        {savingBehavior && <span className="text-muted fs-8 ms-2 fw-normal"><span className="spinner-border spinner-border-sm me-1" role="status"></span>Saving…</span>}
                        {savedFlash === '__policy__' && <span className="text-success fs-8 ms-2 fw-normal"><i className="bi bi-check-circle-fill me-1"></i>Saved</span>}
                    </div>
                    <div className="text-muted fs-8 mb-3">
                        Governs any branch without an explicit choice above — including new branches that show up later.
                    </div>
                    {defaultLocationId ? (
                        <div className="d-flex gap-4">
                            <label className="form-check form-check-custom form-check-solid">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="unmappedBehavior"
                                    checked={unmappedBehavior === 'dont_push'}
                                    disabled={savingBehavior}
                                    onChange={() => handleChangeUnmappedBehavior('dont_push')}
                                />
                                <span className="form-check-label ms-2">Do not push (recommended)</span>
                            </label>
                            <label className="form-check form-check-custom form-check-solid">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="unmappedBehavior"
                                    checked={unmappedBehavior === 'use_default'}
                                    disabled={savingBehavior}
                                    onChange={() => handleChangeUnmappedBehavior('use_default')}
                                />
                                <span className="form-check-label ms-2">Send to default location</span>
                            </label>
                        </div>
                    ) : (
                        <div className="text-muted fs-7">
                            <i className="bi bi-shield-check me-1"></i>
                            No default location is set, so unmatched branches are always <strong>suppressed</strong> (never sent to a fallback).
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

const GetAvailableLocationsGivenAnAccount = ({
    passareOrganization,
    account
}: {
    passareOrganization: PassareOrganization
    account: Account
}) => {

    const [loadingAvailableLocations, setLoadingAvailableLocations] = useState(true)
    const [errorLoadingAvailableLocations, setErrorLoadingAvailableLocations] = useState<string | null>(null)
    const [availableServiceLocations, setAvailableServiceLocations] = useState<AvailableFRMLocation[]>([])

    const [selectedLocationId, setSelectedLocationId] = useState<string | null>(passareOrganization.private_data.frm_linkage_information?.frm_location_id || '')
    const [errorLinkingToFRM, setErrorLinkingToFRM] = useState<string | null>(null)

    const tryToGetAllFRMLocationsForAccountUniqueIdentifier = useCallback(async () => {
        setLoadingAvailableLocations(true)
        try {
            const allFRMLocations = await getAllFRMLocationsForAccountUniqueIdentifier(account.unique_identifier)
            setAvailableServiceLocations(allFRMLocations)
        } catch (exception: any) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'Failed to load available locations'
            setErrorLoadingAvailableLocations(errorMessage)
        } finally {
            setLoadingAvailableLocations(false)
        }
    }, [account.unique_identifier])

    const handleSelectFRMLocationIdForServiceLocationByUniqueIdentifier = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        try {
            await selectFRMLocationForPassareOrganizationByUniqueIdentifier({
                passare_organization_unique_id: passareOrganization.unique_identifier,
                account_unique_id: account.unique_identifier,
                frm_location_id: selectedLocationId
            })
            window.location.reload()
        } catch (exception: any) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'Failed to link Passare organization to FRM'
            setErrorLinkingToFRM(errorMessage)
        }
    }, [selectedLocationId, account.unique_identifier, passareOrganization.unique_identifier])

    const linkOutToFRMWithURL = useMemo(() => {
        //
        // Right now service locations don't have GHL details because it's an admin detail not added to that
        // retrieval. The quickest fix right now is looking up the admin account mapping
        // and deducing the URL from that.
        //
        if (!account) {
            return null
        }
        if (account.external_ghl_site_url) {
            return account.external_ghl_site_url
        }
        return null
    }, [account])

    useEffect(() => {
        tryToGetAllFRMLocationsForAccountUniqueIdentifier()
    }, [tryToGetAllFRMLocationsForAccountUniqueIdentifier])

    if (loadingAvailableLocations) {
        return <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
        </div>
    }

    if (errorLoadingAvailableLocations) {
        return <div className='mt-5'>
            <div className="alert alert-danger" role="alert">
                {errorLoadingAvailableLocations}
            </div>
        </div>
    }

    return <div>
        {availableServiceLocations.length === 0 ? (
            <div className="alert alert-info">
                <div className="alert-text">No available locations found.</div>
            </div>
        ) : (
            <div>
                <div className="mb-5">
                    <label className="form-label fw-bold">Select FRM Location</label>
                    <form onSubmit={handleSelectFRMLocationIdForServiceLocationByUniqueIdentifier}>
                        <Select
                            required
                            className="mb-3"
                            options={[
                                { value: '', label: '- Leave un-mapped -' },
                                ...availableServiceLocations.map((availableServiceLocation) => {
                                    return {
                                        value: availableServiceLocation._id,
                                        label: `${availableServiceLocation.name}`
                                    }
                                })]}
                            onChange={(selectedOption) => {
                                setSelectedLocationId(selectedOption ? selectedOption.value : null)
                            }}
                            value={availableServiceLocations.filter(x => x._id === selectedLocationId).map((availableServiceLocation) => {
                                return {
                                    value: availableServiceLocation._id,
                                    label: `${availableServiceLocation.name}`
                                }
                            })}
                            filterOption={(option, searchValue) => {
                                const words = searchValue.toLowerCase().split(' ');
                                return words.every(word => option.label.toLowerCase().includes(word));
                            }}
                        />
                        <button
                            className='btn btn-primary'
                            type='submit'
                            disabled={!selectedLocationId}
                        >
                            {`Link to ${account.name}${selectedLocationId ? ` - ${availableServiceLocations.find(x => x._id === selectedLocationId)?.name}` : ''}`}
                        </button>
                    </form>
                </div>
                
                {errorLinkingToFRM && (
                    <div className="alert alert-danger mt-3">
                        <div className="alert-text">{errorLinkingToFRM}</div>
                    </div>
                )}
                
                {selectedLocationId && (
                    <div className="mt-5 p-5 bg-light-primary rounded">
                        <h4 className="mb-3">Current FRM Location</h4>
                        <div className="d-flex align-items-center mb-3">
                            <span className="fw-bold me-2">Location ID:</span>
                            <a href={`${linkOutToFRMWithURL}/v2/location/${selectedLocationId}/contacts/smart_list/All`} 
                               target='_blank' 
                               rel='noreferrer'
                               className="text-primary">
                                {selectedLocationId}
                                <i className="bi bi-box-arrow-up-right ms-2"></i>
                            </a>
                        </div>
                        
                        <div className="d-flex gap-3 mt-4">
                            <DisableLinkButton
                                setViewingAvailableLocations={() => {
                                    setSelectedLocationId(null)
                                }}
                                serviceLocationUniqueId={passareOrganization.unique_identifier}
                                onSuccessfullyDisabled={() => {
                                    window.location.reload()
                                }}
                                accountUniqueId={account.unique_identifier}
                            />
                        </div>
                        
                        <CreateWebhooksCustomFolderAndFields
                            linkOutToFRMWithURL={linkOutToFRMWithURL}
                            passareOrganization={passareOrganization}
                        />
                    </div>
                )}
            </div>
        )}
    </div>
}

// JWT Token Management Component
function JWTTokenSection({ passareOrganization, onUpdate }: { passareOrganization: PassareOrganization; onUpdate: () => void }) {
    const [showEditModal, setShowEditModal] = useState(false)
    const [testing, setTesting] = useState(false)
    const [testResult, setTestResult] = useState<{ success: boolean; message: string; organizationName?: string } | null>(null)

    const handleTestConnection = async () => {
        setTesting(true)
        setTestResult(null)
        try {
            const result = await testPassareConnection(passareOrganization.unique_identifier)
            setTestResult({
                success: true,
                message: 'Connection successful!',
                organizationName: result.organization_name
            })
        } catch (error: any) {
            setTestResult({
                success: false,
                message: error.response?.data?.error || error.message || 'Connection failed'
            })
        } finally {
            setTesting(false)
        }
    }

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <label className="fw-bold text-muted">JWT Token Status</label>
                    <div className="d-flex align-items-center mt-2">
                        {passareOrganization.has_api_key ? (
                            <>
                                <span className="badge badge-light-success me-3">Token Present</span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light-success me-2"
                                    onClick={handleTestConnection}
                                    disabled={testing}
                                >
                                    {testing ? 'Testing...' : 'Test Connection'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light-primary"
                                    onClick={() => setShowEditModal(true)}
                                >
                                    Update Token
                                </button>
                            </>
                        ) : (
                            <>
                                <span className="badge badge-light-danger me-3">No Token</span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => setShowEditModal(true)}
                                >
                                    Add Token
                                </button>
                            </>
                        )}
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="fw-bold text-muted">API Configuration</label>
                    <div className="mt-2">
                        <span className="text-muted">JWT token can be updated to refresh API access</span>
                    </div>
                </div>
            </div>

            {testResult && (
                <div className={`alert alert-${testResult.success ? 'success' : 'danger'} mt-4`}>
                    <div className="d-flex align-items-center">
                        <i className={`bi bi-${testResult.success ? 'check-circle' : 'exclamation-triangle'} fs-2 me-3`}></i>
                        <div>
                            <div className="fw-bold">{testResult.message}</div>
                            {testResult.organizationName && (
                                <div className="mt-1">Organization: {testResult.organizationName}</div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Edit JWT Modal */}
            {showEditModal && (
                <EditJWTModal
                    organization={passareOrganization}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={() => {
                        setShowEditModal(false)
                        onUpdate()
                    }}
                />
            )}
        </>
    )
}

// Webhook Reprocessing Component
const WEBHOOK_PAGE_SIZES = [25, 50, 100, 250, 500]

function WebhookReprocessSection({ passareOrganization }: { passareOrganization: PassareOrganization }) {
    const [webhooks, setWebhooks] = useState<PassareWebhookSummary[]>([])
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reprocessingId, setReprocessingId] = useState<string | null>(null)
    const [rowError, setRowError] = useState<Record<string, string>>({})
    const [hydrationFilter, setHydrationFilter] = useState<PassareWebhookHydrationFilter>('all')
    const [pageSize, setPageSize] = useState(250)
    const [page, setPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [hydratedCount, setHydratedCount] = useState(0)
    const [notHydratedCount, setNotHydratedCount] = useState(0)
    const [detailWebhook, setDetailWebhook] = useState<PassareWebhookDetail | null>(null)
    const [detailLoading, setDetailLoading] = useState<string | null>(null)
    const [detailError, setDetailError] = useState<string | null>(null)

    // Filter and page size are passed in (not read from state) to avoid stale
    // closures; pages after the first append, deduped in case new webhooks
    // shifted the offsets.
    const loadWebhooks = useCallback(async (pageToLoad: number, filter: PassareWebhookHydrationFilter, size: number) => {
        if (pageToLoad === 1) setLoading(true)
        else setLoadingMore(true)
        setError(null)
        try {
            const result = await getPassareOrganizationWebhooks(passareOrganization.unique_identifier, pageToLoad, filter, size)
            setWebhooks(prev => pageToLoad === 1
                ? result.webhooks
                : [...prev, ...result.webhooks.filter(w => !prev.some(p => p.unique_identifier === w.unique_identifier))])
            setTotalCount(result.total_count)
            setHydratedCount(result.hydrated_count)
            setNotHydratedCount(result.not_hydrated_count)
            setPage(pageToLoad)
        } catch (exception: any) {
            console.error(exception)
            setError(exception instanceof APIError ? exception.message : 'Failed to load webhooks')
        } finally {
            if (pageToLoad === 1) setLoading(false)
            else setLoadingMore(false)
        }
    }, [passareOrganization.unique_identifier])

    useEffect(() => {
        setHydrationFilter('all')
        setPageSize(250)
        loadWebhooks(1, 'all', 250)
    }, [loadWebhooks])

    const handleFilterChange = useCallback((filter: PassareWebhookHydrationFilter) => {
        setHydrationFilter(filter)
        setWebhooks([])
        loadWebhooks(1, filter, pageSize)
    }, [loadWebhooks, pageSize])

    const handlePageSizeChange = useCallback((size: number) => {
        setPageSize(size)
        setWebhooks([])
        loadWebhooks(1, hydrationFilter, size)
    }, [loadWebhooks, hydrationFilter])

    const handleReprocessOne = useCallback(async (webhookId: string) => {
        setReprocessingId(webhookId)
        setRowError(prev => { const next = { ...prev }; delete next[webhookId]; return next })
        try {
            const updated = await reprocessPassareWebhook(passareOrganization.unique_identifier, webhookId)
            // Update this row, and any sibling rows for the same case/acquaintance
            // (case hydration is per-case, husk status is per-acquaintance).
            setWebhooks(prev => prev.map(w => {
                if (w.unique_identifier === updated.unique_identifier) return updated
                let next = w
                if (updated.case_uuid && w.case_uuid === updated.case_uuid) {
                    next = { ...next, case_hydrated: updated.case_hydrated }
                }
                if (updated.acquaintance_uuid && w.acquaintance_uuid === updated.acquaintance_uuid) {
                    next = { ...next, acquaintance_hydrated: updated.acquaintance_hydrated }
                }
                return next
            }))
        } catch (exception: any) {
            console.error(exception)
            const message = exception instanceof APIError ? exception.message : 'Reprocess failed'
            setRowError(prev => ({ ...prev, [webhookId]: message }))
        } finally {
            setReprocessingId(null)
        }
    }, [passareOrganization.unique_identifier])

    const handleViewDetail = useCallback(async (webhookId: string) => {
        setDetailLoading(webhookId)
        setDetailError(null)
        try {
            const detail = await getPassareWebhookDetail(passareOrganization.unique_identifier, webhookId)
            setDetailWebhook(detail)
        } catch (exception: any) {
            console.error(exception)
            setDetailError(exception instanceof APIError ? exception.message : 'Failed to load webhook payload')
        } finally {
            setDetailLoading(null)
        }
    }, [passareOrganization.unique_identifier])

    if (!passareOrganization.has_api_key) {
        return (
            <div className="alert alert-warning">
                <div className="alert-text">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>JWT Token Required:</strong> A valid JWT token must be configured before webhooks can be reprocessed.
                </div>
            </div>
        )
    }

    const totalAll = hydratedCount + notHydratedCount

    return (
        <div>
            <p className="text-muted mb-3">
                Every received webhook for this organization. Reprocess one to re-fetch its case
                details from Passare using the current token — useful when the data didn't hydrate when
                the webhook first arrived. "Hydrated" means the linked case now has its full details;
                a red "Husk" badge means the acquaintance was never enriched, so that lead stays
                invisible to the lead pages until reprocessed.
            </p>

            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                <div className="text-muted fs-7">
                    {totalAll} webhook{totalAll === 1 ? '' : 's'} ·{' '}
                    <span className="text-success fw-bold">{hydratedCount} hydrated</span> ·{' '}
                    <span className="text-danger fw-bold">{notHydratedCount} not yet</span>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="btn-group" role="group" aria-label="Hydration filter">
                        {([
                            { value: 'all', label: `All (${totalAll})` },
                            { value: 'hydrated', label: `Hydrated (${hydratedCount})` },
                            { value: 'not_hydrated', label: `Not hydrated (${notHydratedCount})` },
                        ] as { value: PassareWebhookHydrationFilter; label: string }[]).map(option => (
                            <button
                                key={option.value}
                                type="button"
                                className={`btn btn-sm ${hydrationFilter === option.value ? 'btn-primary' : 'btn-light'}`}
                                disabled={loading || loadingMore}
                                onClick={() => handleFilterChange(option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <select
                        className="form-select form-select-sm w-auto"
                        aria-label="Webhooks per page"
                        value={pageSize}
                        disabled={loading || loadingMore}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    >
                        {WEBHOOK_PAGE_SIZES.map(size => (
                            <option key={size} value={size}>{size} / page</option>
                        ))}
                    </select>
                    <button className="btn btn-sm btn-light" onClick={() => loadWebhooks(1, hydrationFilter, pageSize)} disabled={loading || loadingMore}>
                        <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                    </button>
                </div>
            </div>

            {error && <div className="alert alert-danger"><div className="alert-text">{error}</div></div>}

            {loading ? (
                <div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div>
            ) : webhooks.length === 0 ? (
                <div className="alert alert-info">
                    <div className="alert-text">
                        {hydrationFilter !== 'all'
                            ? 'No webhooks match this filter.'
                            : 'No webhooks found for this organization.'}
                    </div>
                </div>
            ) : (
                <>
                <div className="table-responsive">
                    <table className="table table-row-bordered table-row-gray-200 align-middle gs-0 gy-2">
                        <thead>
                            <tr className="fw-bold text-muted fs-7">
                                <th className="min-w-150px">Event / Trigger</th>
                                <th className="min-w-200px">Case · Branch</th>
                                <th className="min-w-150px">Received</th>
                                <th className="min-w-100px">Status</th>
                                <th className="min-w-100px text-end">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {webhooks.map(w => (
                                <tr key={w.unique_identifier}>
                                    <td>
                                        <div className="fw-bold">{w.trigger_type || '(unknown)'}</div>
                                        <div className="text-muted fs-8">{w.event_id || w.unique_identifier}</div>
                                    </td>
                                    <td>
                                        <div className="fs-7">
                                            {w.case_uuid
                                                ? <code>{w.case_uuid.slice(0, 8)}…</code>
                                                : <span className="text-muted">— no case —</span>}
                                        </div>
                                        <div className="text-muted fs-8">{w.branch_name || ''}</div>
                                    </td>
                                    <td className="text-muted fs-7">{new Date(w.inserted_at).toLocaleString()}</td>
                                    <td>
                                        {w.case_hydrated
                                            ? <span className="badge badge-light-success">Hydrated</span>
                                            : <span className="badge badge-light-warning">Not hydrated</span>}
                                        {w.acquaintance_hydrated === false && (
                                            <span className="badge badge-light-danger ms-1" title="Acquaintance record is a husk — no hydrated family/contact details">Husk</span>
                                        )}
                                        {rowError[w.unique_identifier] && (
                                            <div className="text-danger fs-8 mt-1">{rowError[w.unique_identifier]}</div>
                                        )}
                                    </td>
                                    <td className="text-end">
                                        <button
                                            className="btn btn-sm btn-light me-2"
                                            disabled={detailLoading === w.unique_identifier}
                                            onClick={() => handleViewDetail(w.unique_identifier)}
                                        >
                                            {detailLoading === w.unique_identifier
                                                ? <span className="spinner-border spinner-border-sm" role="status"></span>
                                                : <><i className="bi bi-eye me-1"></i>View</>}
                                        </button>
                                        <button
                                            className="btn btn-sm btn-light-primary"
                                            disabled={reprocessingId === w.unique_identifier}
                                            onClick={() => handleReprocessOne(w.unique_identifier)}
                                        >
                                            {reprocessingId === w.unique_identifier ? (
                                                <><span className="spinner-border spinner-border-sm me-1" role="status"></span>…</>
                                            ) : (
                                                <><i className="bi bi-arrow-clockwise me-1"></i>Reprocess</>
                                            )}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="d-flex align-items-center justify-content-between mt-2">
                    <span className="text-muted fs-8">Showing {webhooks.length} of {totalCount}</span>
                    {webhooks.length < totalCount && (
                        <button
                            className="btn btn-sm btn-light"
                            disabled={loadingMore}
                            onClick={() => loadWebhooks(page + 1, hydrationFilter, pageSize)}
                        >
                            {loadingMore
                                ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Loading…</>
                                : <><i className="bi bi-chevron-down me-1"></i>Load More</>}
                        </button>
                    )}
                </div>
                </>
            )}

            {detailError && (
                <div className="alert alert-danger mt-3"><div className="alert-text">{detailError}</div></div>
            )}

            {detailWebhook && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1090 }}></div>
                    <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1095 }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Webhook payload · {detailWebhook.trigger_type || '(unknown)'}</h5>
                                    <button type="button" className="btn-close" onClick={() => setDetailWebhook(null)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="text-muted fs-8 mb-3">{detailWebhook.event_id || detailWebhook.unique_identifier}</div>

                                    <div className="fw-bold mb-1">Raw payload (request_body)</div>
                                    <pre className="bg-light p-3 rounded mb-4" style={{ maxHeight: 280, overflow: 'auto', fontSize: 12 }}>
                                        {JSON.stringify(detailWebhook.request_body ?? {}, null, 2)}
                                    </pre>

                                    <div className="fw-bold mb-1">Parsed payload</div>
                                    <pre className="bg-light p-3 rounded mb-4" style={{ maxHeight: 280, overflow: 'auto', fontSize: 12 }}>
                                        {JSON.stringify(detailWebhook.parsed_payload ?? {}, null, 2)}
                                    </pre>

                                    <div className="fw-bold mb-1">Hydrated case result</div>
                                    {detailWebhook.case_result && JSON.stringify(detailWebhook.case_result) !== '{}' ? (
                                        <pre className="bg-light p-3 rounded mb-2" style={{ maxHeight: 280, overflow: 'auto', fontSize: 12 }}>
                                            {JSON.stringify(detailWebhook.case_result, null, 2)}
                                        </pre>
                                    ) : (
                                        <div className="text-muted fs-7 mb-2">Not hydrated yet — reprocess this webhook to fetch the case data.</div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-light" onClick={() => setDetailWebhook(null)}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

// Edit JWT Modal Component (moved from PassareManagement.tsx)
function EditJWTModal({ 
    organization, 
    onClose, 
    onSuccess 
}: { 
    organization: PassareOrganization
    onClose: () => void
    onSuccess: () => void 
}) {
    const [jwt, setJWT] = useState('')
    const [testing, setTesting] = useState(false)
    const [saving, setSaving] = useState(false)
    const [testResult, setTestResult] = useState<{ success: boolean; message: string; organizationName?: string } | null>(null)

    const handleTestConnection = async () => {
        setTesting(true)
        setTestResult(null)
        try {
            const result = await testPassareConnection(organization.unique_identifier, jwt)
            setTestResult({
                success: true,
                message: 'Connection successful!',
                organizationName: result.organization_name
            })
        } catch (error: any) {
            setTestResult({
                success: false,
                message: error.response?.data?.error || error.message || 'Connection failed'
            })
        } finally {
            setTesting(false)
        }
    }

    const handleSave = async () => {
        setSaving(true)
        try {
            await updatePassareOrganization(organization.unique_identifier, { api_key: jwt })
            onSuccess()
        } catch (error: any) {
            setTestResult({
                success: false,
                message: `Failed to update JWT: ${error.response?.data?.error || error.message}`
            })
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
            <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1050 }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content" style={{ zIndex: 1060 }}>
                        <div className="modal-header">
                            <h5 className="modal-title">Update JWT Token</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label className="form-label">Organization</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={organization.organization_name}
                                    disabled
                                />
                            </div>
                            <div className="mb-3">
                                <label className="form-label">Current JWT Status</label>
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        {organization.has_api_key ? (
                                            <span className="badge badge-light-success">JWT Present</span>
                                        ) : (
                                            <span className="badge badge-light-danger">No JWT Token</span>
                                        )}
                                    </div>
                                    {organization.has_api_key && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light-success"
                                            onClick={async () => {
                                                setTesting(true)
                                                setTestResult(null)
                                                try {
                                                    const result = await testPassareConnection(organization.unique_identifier)
                                                    setTestResult({
                                                        success: true,
                                                        message: 'Connection successful!',
                                                        organizationName: result.organization_name
                                                    })
                                                } catch (error: any) {
                                                    setTestResult({
                                                        success: false,
                                                        message: error.response?.data?.error || error.message || 'Connection failed'
                                                    })
                                                } finally {
                                                    setTesting(false)
                                                }
                                            }}
                                            disabled={testing}
                                        >
                                            Test Current JWT
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="mb-3">
                                <label className="form-label required">New JWT Token</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={jwt}
                                    onChange={(e) => setJWT(e.target.value)}
                                    placeholder="Paste the new JWT token from Passare"
                                />
                            </div>
                            {testResult && (
                                <div className={`alert alert-${testResult.success ? 'success' : 'danger'} mb-3`}>
                                    <div className="d-flex align-items-center">
                                        <i className={`bi bi-${testResult.success ? 'check-circle' : 'exclamation-triangle'} fs-2 me-3`}></i>
                                        <div>
                                            <div className="fw-bold">{testResult.message}</div>
                                            {testResult.organizationName && (
                                                <div className="mt-1">Organization: {testResult.organizationName}</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="d-flex justify-content-end">
                                <button
                                    type="button"
                                    className="btn btn-light-primary"
                                    onClick={handleTestConnection}
                                    disabled={!jwt || testing}
                                >
                                    {testing ? 'Testing...' : 'Test Connection'}
                                </button>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-light" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={!jwt || saving}
                            >
                                {saving ? 'Updating...' : 'Update JWT'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

function PassareOrganizationDetail({ isEmbedded = false }: { isEmbedded?: boolean }) {

    const { passare_organization_unique_identifier: passareOrganizationUniqueIdentifier } = useParams<{
        passare_organization_unique_identifier: string
    }>()
    
    const { currentUser } = useAuth()
    const isAdmin = !!currentUser?.is_admin

    const [passareOrganization, setPassareOrganization] = useState<PassareOrganization | null>(null);
    const [loadingPassareOrganizations, setLoadingPassareOrganization] = useState(true);
    const [errorLoadingPassareOrganizations, setError] = useState<string | null>(null);
    const [updatingAutoSync, setUpdatingAutoSync] = useState(false);
    const [activeTab, setActiveTab] = useState<'general' | 'tags' | 'admin'>('general');
    const [showOffboardConfirm, setShowOffboardConfirm] = useState(false);
    const [updatingOffboard, setUpdatingOffboard] = useState(false);
    const [offboardError, setOffboardError] = useState<string | null>(null);

    // Advanced raw-flags editor (admin/debug). Local state is batched and
    // applied with "Save Flags"; it is re-synced from the server on every load.
    const [localFlags, setLocalFlags] = useState<{
        active: boolean
        is_inactive: boolean
        is_archived: boolean
        auto_sync_enabled: boolean
    }>({ active: true, is_inactive: false, is_archived: false, auto_sync_enabled: false });
    const [updatingFlags, setUpdatingFlags] = useState(false);
    const [flagsError, setFlagsError] = useState<string | null>(null);
    
    // Local form state for auto-sync settings
    const [localAutoSyncEnabled, setLocalAutoSyncEnabled] = useState<boolean>(false);
    const [localAutoSyncDelayDays, setLocalAutoSyncDelayDays] = useState<number>(7);
    const [localAutoSyncMaxLeadAgeDays, setLocalAutoSyncMaxLeadAgeDays] = useState<number>(14);
    const [localAutoSyncMaxLeadAgeEnabled, setLocalAutoSyncMaxLeadAgeEnabled] = useState<boolean>(true);
    // Daily UTC window the nightly cron may run in (start inclusive, end exclusive
    // vs the run hour). 13–24 UTC = 9 AM–7 PM Eastern.
    const [localAutoSyncStartHour, setLocalAutoSyncStartHour] = useState<number>(13);
    const [localAutoSyncEndHour, setLocalAutoSyncEndHour] = useState<number>(24);
    const [autoSyncError, setAutoSyncError] = useState<string | null>(null);

    const [selectedAccount, setSelectedAccount] = useState<Account | null>(null)

    const {
        accounts,
        loadingAccounts,
    } = useAccounts()

    const loadPassareOrganization = useCallback(async () => {
        if (!passareOrganizationUniqueIdentifier) {
            return
        }
        if (loadingAccounts) {
            return
        }
        setLoadingPassareOrganization(true)
        setError(null)
        try {
            const updatedPassareOrganization = await getPassareOrganizationByUniqueIdentifier(passareOrganizationUniqueIdentifier)
            setPassareOrganization(updatedPassareOrganization)
            
            // Initialize local form state
            setLocalAutoSyncEnabled(updatedPassareOrganization.auto_sync_enabled || false)
            setLocalAutoSyncDelayDays(updatedPassareOrganization.auto_sync_delay_days || 7)
            setLocalAutoSyncMaxLeadAgeDays(updatedPassareOrganization.auto_sync_max_lead_age_days || 14)
            // ?? (not ||): false is a real, saved opt-out and must survive the reload
            setLocalAutoSyncMaxLeadAgeEnabled(updatedPassareOrganization.auto_sync_max_lead_age_enabled ?? true)
            // ?? (not ||): 0 is a legitimate start hour and must not fall back to the default
            setLocalAutoSyncStartHour(updatedPassareOrganization.auto_sync_start_hour ?? 13)
            setLocalAutoSyncEndHour(updatedPassareOrganization.auto_sync_end_hour ?? 24)
            setLocalFlags({
                active: updatedPassareOrganization.active ?? true,
                is_inactive: updatedPassareOrganization.is_inactive ?? false,
                is_archived: updatedPassareOrganization.is_archived ?? false,
                auto_sync_enabled: updatedPassareOrganization.auto_sync_enabled ?? false,
            })
            
            if (updatedPassareOrganization.private_data.frm_linkage_information?.account_unique_identifier) {
                const selectedAccount = accounts.find(account => account.unique_identifier === updatedPassareOrganization.private_data.frm_linkage_information?.account_unique_identifier)
                setSelectedAccount(selectedAccount || null)
            }
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'Failed to load Passare organization'
            setError(errorMessage)
        } finally {
            setLoadingPassareOrganization(false)
        }
    }, [passareOrganizationUniqueIdentifier, loadingAccounts, accounts])

    useEffect(() => {
        loadPassareOrganization()
    }, [loadPassareOrganization])

    // Check if there are unsaved changes
    const hasUnsavedChanges = () => {
        if (!passareOrganization) return false

        const serverAutoSyncEnabled = passareOrganization.auto_sync_enabled || false
        const serverAutoSyncDelayDays = passareOrganization.auto_sync_delay_days || 7
        const serverAutoSyncMaxLeadAgeDays = passareOrganization.auto_sync_max_lead_age_days || 14
        const serverAutoSyncMaxLeadAgeEnabled = passareOrganization.auto_sync_max_lead_age_enabled ?? true
        const serverAutoSyncStartHour = passareOrganization.auto_sync_start_hour ?? 13
        const serverAutoSyncEndHour = passareOrganization.auto_sync_end_hour ?? 24

        return localAutoSyncEnabled !== serverAutoSyncEnabled ||
               localAutoSyncDelayDays !== serverAutoSyncDelayDays ||
               localAutoSyncMaxLeadAgeDays !== serverAutoSyncMaxLeadAgeDays ||
               localAutoSyncMaxLeadAgeEnabled !== serverAutoSyncMaxLeadAgeEnabled ||
               localAutoSyncStartHour !== serverAutoSyncStartHour ||
               localAutoSyncEndHour !== serverAutoSyncEndHour
    }

    // Save auto-sync settings
    const saveAutoSyncSettings = async () => {
        if (!passareOrganization) return

        setAutoSyncError(null)
        // Mirrors the backend cross-check: a stop value at or below the start
        // means every lead is already past the cap the moment it becomes eligible.
        if (localAutoSyncMaxLeadAgeDays <= localAutoSyncDelayDays) {
            setAutoSyncError('"Days After Death to Stop Syncing" must be greater than "Days After Death to Start Syncing" — otherwise nothing could ever auto-sync.')
            return
        }
        // Mirrors the backend hour-window check: an empty window never runs.
        if (localAutoSyncStartHour >= localAutoSyncEndHour) {
            setAutoSyncError('The sync window\'s start hour must be before its end hour.')
            return
        }
        setUpdatingAutoSync(true)
        try {
            await updatePassareOrganization(
                passareOrganization.unique_identifier,
                {
                    auto_sync_enabled: localAutoSyncEnabled,
                    auto_sync_delay_days: localAutoSyncDelayDays,
                    auto_sync_max_lead_age_days: localAutoSyncMaxLeadAgeDays,
                    auto_sync_max_lead_age_enabled: localAutoSyncMaxLeadAgeEnabled,
                    auto_sync_start_hour: localAutoSyncStartHour,
                    auto_sync_end_hour: localAutoSyncEndHour
                }
            )

            // Re-seed all local form state from server truth (the backend can
            // reject writes, so never patch local state optimistically).
            await loadPassareOrganization()
        } catch (error) {
            console.error('Failed to save auto-sync settings:', error)
            setAutoSyncError(error instanceof APIError ? error.message : 'Failed to save auto-sync settings. Please try again.')
        } finally {
            setUpdatingAutoSync(false)
        }
    }

    // Offboard (deactivate) or reinstate the organization. Offboarding closes
    // every push path: it stops the nightly cron and blocks the manual "Sync"
    // button, and hides the org from the leads/management lists.
    const applyOffboarded = async (offboarded: boolean) => {
        if (!passareOrganization) return

        setUpdatingOffboard(true)
        setOffboardError(null)
        try {
            await updatePassareOrganization(passareOrganization.unique_identifier, { offboarded })
            setShowOffboardConfirm(false)
            await loadPassareOrganization()
        } catch (error: any) {
            setOffboardError(error?.response?.data?.error || error?.message || 'Failed to update organization')
        } finally {
            setUpdatingOffboard(false)
        }
    }

    const hasFlagChanges = () => {
        if (!passareOrganization) return false
        return localFlags.active !== (passareOrganization.active ?? true)
            || localFlags.is_inactive !== (passareOrganization.is_inactive ?? false)
            || localFlags.is_archived !== (passareOrganization.is_archived ?? false)
            || localFlags.auto_sync_enabled !== (passareOrganization.auto_sync_enabled ?? false)
    }

    const saveFlags = async () => {
        if (!passareOrganization) return

        setUpdatingFlags(true)
        setFlagsError(null)
        try {
            await updatePassareOrganization(passareOrganization.unique_identifier, { flags: localFlags })
            await loadPassareOrganization()
        } catch (error: any) {
            setFlagsError(error?.response?.data?.error || error?.message || 'Failed to update flags')
        } finally {
            setUpdatingFlags(false)
        }
    }

    if (loadingPassareOrganizations || loadingAccounts) {
        return <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
        </div>
    }

    if (errorLoadingPassareOrganizations || !passareOrganization) {
        return <div>
            <div className="alert alert-danger" role="alert">
                {errorLoadingPassareOrganizations || 'Failed to load Passare organization (501)'}
            </div>
            <div className='mt-5'>
                <button className='btn btn-primary btn-lg' onClick={loadPassareOrganization}>
                    Retry
                </button>
            </div>
        </div>
    }

    return <PassareOrganizationDetailContext.Provider value={{
        passareOrganization,
    }}>
        {!isEmbedded && (
            <PageTitle breadcrumbs={[
                { title: 'All Leads', path: '/debugging/all-leads', isActive: false },
                { title: 'Passare', path: '/debugging/all-leads/passare-acquaintances', isActive: false },
                { title: 'Manage Organizations', path: '/debugging/all-leads/passare-acquaintances/manage', isActive: false },
                { title: passareOrganization.organization_name, path: '', isActive: true }
            ]}>
                {passareOrganization.organization_name}
            </PageTitle>
        )}
        
        <div className={isEmbedded ? "" : "d-flex flex-column gap-7 gap-lg-10"}>
            {/* Organization Info Card */}
            <KTCard>
                <div className="card-header">
                    <h3 className="card-title">Organization Details</h3>
                    {!isEmbedded && (
                        <div className="card-toolbar">
                            <Link to="/debugging/all-leads/passare-acquaintances/manage" className="btn btn-sm btn-light">
                                <i className="bi bi-arrow-left fs-3 me-2"></i>
                                Back to Organizations
                            </Link>
                        </div>
                    )}
                </div>
                <KTCardBody>
                    <div className="row mb-5">
                        <div className="col-md-4">
                            <label className="fw-bold text-muted">Organization Name</label>
                            <div className="fw-bolder fs-6 text-gray-800">{passareOrganization.organization_name}</div>
                        </div>
                        <div className="col-md-4">
                            <label className="fw-bold text-muted">Organization UUID</label>
                            <div className="fw-bolder fs-6 text-gray-800">{passareOrganization.organization_uuid}</div>
                        </div>
                        <div className="col-md-4">
                            <label className="fw-bold text-muted">Unique Identifier</label>
                            <div className="fw-bolder fs-6 text-gray-800">{passareOrganization.unique_identifier}</div>
                        </div>
                    </div>
                </KTCardBody>
            </KTCard>

            {/* Tab Navigation */}
            <div className="card">
                <div className="card-header card-header-stretch">
                    <div className="card-toolbar m-0">
                        <ul className="nav nav-tabs nav-line-tabs nav-stretch border-transparent fs-5 fw-bold">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'general' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('general')}
                                >
                                    General Settings
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'tags' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('tags')}
                                >
                                    Tags
                                </button>
                            </li>
                            {isAdmin && (
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === 'admin' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('admin')}
                                    >
                                        Admin Settings
                                    </button>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'general' ? (
                <>


                    {/* Auto-Sync Configuration Card */}
                    <KTCard>
                        <div className="card-header">
                            <h3 className="card-title">Automatic Lead Syncing</h3>
                        </div>
                <KTCardBody>
                    <div className="form-check form-switch mb-5">
                        <input 
                            className="form-check-input" 
                            type="checkbox" 
                            id="autoSyncEnabled" 
                            checked={localAutoSyncEnabled}
                            disabled={updatingAutoSync}
                            onChange={(e) => {
                                setLocalAutoSyncEnabled(e.target.checked)
                            }}
                        />
                        <label className="form-check-label fw-bold" htmlFor="autoSyncEnabled">
                            Enable Automatic Lead Syncing to 360
                            {updatingAutoSync && (
                                <span className="ms-2">
                                    <div className="spinner-border spinner-border-sm" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </span>
                            )}
                        </label>
                    </div>

                    {localAutoSyncEnabled && (
                        <div className="bg-light p-4 rounded">
                            <div className="row">
                                <div className="col-md-6">
                                    <label className="form-label fw-bold">Days After Death to Start Syncing</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={localAutoSyncDelayDays}
                                        min="1"
                                        max="30"
                                        disabled={updatingAutoSync}
                                        onChange={(e) => {
                                            const numValue = parseInt(e.target.value)
                                            setLocalAutoSyncDelayDays(isNaN(numValue) ? 1 : numValue)
                                        }}
                                    />
                                    <div className="form-text">A lead becomes eligible to auto-sync this many days after the date of death (default 7).</div>
                                </div>
                                <div className="col-md-6">
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <label className="form-label fw-bold mb-0">Days After Death to Stop Syncing</label>
                                        <div className="form-check form-switch mb-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="autoSyncMaxLeadAgeEnabled"
                                                checked={localAutoSyncMaxLeadAgeEnabled}
                                                disabled={updatingAutoSync}
                                                onChange={(e) => {
                                                    setLocalAutoSyncMaxLeadAgeEnabled(e.target.checked)
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor="autoSyncMaxLeadAgeEnabled">
                                                {localAutoSyncMaxLeadAgeEnabled ? 'Enforced' : 'Off'}
                                            </label>
                                        </div>
                                    </div>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={localAutoSyncMaxLeadAgeDays}
                                        min="1"
                                        max="365"
                                        disabled={updatingAutoSync || !localAutoSyncMaxLeadAgeEnabled}
                                        onChange={(e) => {
                                            const numValue = parseInt(e.target.value)
                                            setLocalAutoSyncMaxLeadAgeDays(isNaN(numValue) ? 14 : numValue)
                                        }}
                                    />
                                    <div className="form-text">
                                        {localAutoSyncMaxLeadAgeEnabled
                                            ? 'Once the date of death is more than this many days ago, the lead is no longer auto-synced (default 14). Manual "Sync to 360" is unaffected.'
                                            : 'Stale-lead cap disabled — the nightly sync will not age-filter this org\'s leads. The June 1 go-live floor still applies (and a 90-day safety limit).'}
                                    </div>
                                </div>
                            </div>

                            <div className="row mt-4">
                                <div className="col-12">
                                    <label className="form-label fw-bold mb-1">Daily Sync Window (UTC hours)</label>
                                    <div className="d-flex align-items-center gap-2" style={{maxWidth: '320px'}}>
                                        <input
                                            type="number"
                                            className="form-control"
                                            aria-label="Sync window start hour (UTC)"
                                            value={localAutoSyncStartHour}
                                            min="0"
                                            max="23"
                                            disabled={updatingAutoSync}
                                            onChange={(e) => {
                                                const numValue = parseInt(e.target.value)
                                                setLocalAutoSyncStartHour(isNaN(numValue) ? 13 : numValue)
                                            }}
                                        />
                                        <span className="text-muted">to</span>
                                        <input
                                            type="number"
                                            className="form-control"
                                            aria-label="Sync window end hour (UTC)"
                                            value={localAutoSyncEndHour}
                                            min="1"
                                            max="24"
                                            disabled={updatingAutoSync}
                                            onChange={(e) => {
                                                const numValue = parseInt(e.target.value)
                                                setLocalAutoSyncEndHour(isNaN(numValue) ? 24 : numValue)
                                            }}
                                        />
                                    </div>
                                    <div className="form-text">
                                        The nightly auto-sync only runs during these UTC hours (start inclusive, end exclusive). Default 13–24 UTC = 9 AM–7 PM Eastern. Leave at the default unless this org needs a different window.
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Inline save/validation errors (never browser alerts) */}
                    {autoSyncError && (
                        <div className="alert alert-danger mt-4 mb-0" role="alert">
                            {autoSyncError}
                        </div>
                    )}

                    {/* Show warning banner only when there are unsaved changes */}
                    {hasUnsavedChanges() && (
                        <div className="d-flex align-items-center mt-4 p-3 bg-warning bg-opacity-10 border border-warning rounded">
                            <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                            <span className="text-warning fw-bold">You have unsaved changes</span>
                        </div>
                    )}

                    {/* Always show save button, but disable when no changes */}
                    <div className="d-flex justify-content-end mt-4">
                        <button 
                            className="btn btn-primary"
                            onClick={saveAutoSyncSettings}
                            disabled={updatingAutoSync || !hasUnsavedChanges()}
                        >
                            {updatingAutoSync ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                    Saving...
                                </>
                            ) : (
                                'Save Settings'
                            )}
                        </button>
                    </div>

                    {!passareOrganization.has_api_key && (
                        <div className="alert alert-warning mt-4">
                            <div className="alert-text">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                <strong>JWT Token Required:</strong> Auto-sync requires a valid JWT token to be configured for this organization.
                            </div>
                        </div>
                    )}
                        </KTCardBody>
                    </KTCard>

                    {/* Account Linking Card */}
                    <KTCard>
                        <div className="card-header">
                            <h3 className="card-title">Account & Location Linking</h3>
                        </div>
                <KTCardBody>
                    <div className="mb-5">
                        <label className="form-label fw-bold">Select Account to Link</label>
                        <select 
                            className='form-select form-select-solid' 
                            onChange={(e) => {
                                const selectedAccount = accounts.find(account => account.unique_identifier === e.target.value)
                                setSelectedAccount(selectedAccount || null)
                            }} 
                            value={selectedAccount ? (selectedAccount as Account)?.unique_identifier : ''}
                        >
                            <option value='' disabled>Select an account...</option>
                            {accounts.map((account) => (
                                <option key={account.unique_identifier} value={account.unique_identifier}>
                                    {account.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    
                    {!selectedAccount ? (
                        <div className="alert alert-info">
                            <div className="alert-text">
                                Please select an account to continue with the linking process.
                            </div>
                        </div>
                    ) : (
                        <>
                            {!selectedAccount.private_data?.linked_to_frm_company_id ? (
                                <div className="alert alert-warning">
                                    <div className="alert-text">
                                        <strong>Action Required:</strong> The selected account is not linked to a FRM company.
                                        <br />
                                        <Link to={`/accounts/${selectedAccount.unique_identifier}/admin`} className="btn btn-sm btn-warning mt-3">
                                            Link Account to FRM Company
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <GetAvailableLocationsGivenAnAccount
                                    passareOrganization={passareOrganization}
                                    account={selectedAccount}
                                />
                            )}
                        </>
                    )}
                        </KTCardBody>
                    </KTCard>

                    {/* Branch Routing Card - shared-token orgs */}
                    <KTCard>
                        <div className="card-header">
                            <h3 className="card-title">Branch Routing</h3>
                            <div className="card-toolbar">
                                <span className="badge badge-light-info">Shared-token orgs</span>
                            </div>
                        </div>
                        <KTCardBody>
                            <BranchRoutingSection passareOrganization={passareOrganization} accounts={accounts} />
                        </KTCardBody>
                    </KTCard>
                </>
            ) : activeTab === 'tags' ? (
                <PassareOrganizationTagsTab passareOrganization={passareOrganization} />
            ) : (
                <>
                    {/* JWT Token Management Card - Admin Only */}
                    <KTCard>
                        <div className="card-header">
                            <h3 className="card-title">JWT Token Management</h3>
                        </div>
                        <KTCardBody>
                            <JWTTokenSection passareOrganization={passareOrganization} onUpdate={loadPassareOrganization} />
                        </KTCardBody>
                    </KTCard>

                    {/* Webhook Reprocessing Card - Admin Only */}
                    <KTCard>
                        <div className="card-header">
                            <h3 className="card-title">Webhook Management</h3>
                        </div>
                        <KTCardBody>
                            <WebhookReprocessSection passareOrganization={passareOrganization} />
                        </KTCardBody>
                    </KTCard>

                    {/* Advanced Flags - Admin Only */}
                    <KTCard>
                        <div className="card-header">
                            <h3 className="card-title">Advanced Flags</h3>
                        </div>
                        <KTCardBody>
                            <div className="text-muted fs-7 mb-5">
                                Raw per-flag control for admin/debug use. For normal offboarding use the Danger Zone
                                below — it sets a safe combination of these. Setting flags individually can create
                                unusual states (e.g. hidden but still selected by the cron).
                            </div>
                            {flagsError && (
                                <div className="alert alert-danger d-flex align-items-center">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <span>{flagsError}</span>
                                </div>
                            )}

                            <div className="form-check form-switch form-check-custom form-check-solid mb-4">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="flagActive"
                                    checked={localFlags.active}
                                    disabled={updatingFlags}
                                    onChange={(e) => setLocalFlags((f) => ({ ...f, active: e.target.checked }))}
                                />
                                <label className="form-check-label" htmlFor="flagActive">
                                    <span className="fw-bold text-gray-800">active</span>
                                    <span className="d-block text-muted fs-8">Master on/off — when off, the nightly auto-sync skips this org.</span>
                                </label>
                            </div>

                            <div className="form-check form-switch form-check-custom form-check-solid mb-4">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="flagAutoSync"
                                    checked={localFlags.auto_sync_enabled}
                                    disabled={updatingFlags}
                                    onChange={(e) => setLocalFlags((f) => ({ ...f, auto_sync_enabled: e.target.checked }))}
                                />
                                <label className="form-check-label" htmlFor="flagAutoSync">
                                    <span className="fw-bold text-gray-800">auto_sync_enabled</span>
                                    <span className="d-block text-muted fs-8">Nightly automatic push to the 360. Also configurable (with a delay) on the General tab.</span>
                                </label>
                            </div>

                            <div className="form-check form-switch form-check-custom form-check-solid mb-4">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="flagInactive"
                                    checked={localFlags.is_inactive}
                                    disabled={updatingFlags}
                                    onChange={(e) => setLocalFlags((f) => ({ ...f, is_inactive: e.target.checked }))}
                                />
                                <label className="form-check-label" htmlFor="flagInactive">
                                    <span className="fw-bold text-gray-800">is_inactive</span>
                                    <span className="d-block text-muted fs-8">Hides the org from the leads list and management list, and blocks the manual "Sync" button.</span>
                                </label>
                            </div>

                            <div className="form-check form-switch form-check-custom form-check-solid mb-5">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="flagArchived"
                                    checked={localFlags.is_archived}
                                    disabled={updatingFlags}
                                    onChange={(e) => setLocalFlags((f) => ({ ...f, is_archived: e.target.checked }))}
                                />
                                <label className="form-check-label" htmlFor="flagArchived">
                                    <span className="fw-bold text-gray-800">is_archived</span>
                                    <span className="d-block text-muted fs-8">Marks the org archived. Hidden from the management list (counts toward "offboarded").</span>
                                </label>
                            </div>

                            <div className="d-flex justify-content-end">
                                <button
                                    className="btn btn-primary"
                                    onClick={saveFlags}
                                    disabled={updatingFlags || !hasFlagChanges()}
                                >
                                    {updatingFlags ? (
                                        <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Saving...</>
                                    ) : (
                                        'Save Flags'
                                    )}
                                </button>
                            </div>
                        </KTCardBody>
                    </KTCard>

                    {/* Danger Zone - Admin Only */}
                    <KTCard className="border border-danger border-dashed">
                        <div className="card-header">
                            <h3 className="card-title text-danger">Danger Zone</h3>
                        </div>
                        <KTCardBody>
                            {offboardError && (
                                <div className="alert alert-danger d-flex align-items-center">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <span>{offboardError}</span>
                                </div>
                            )}
                            {passareOrganization.offboarded ? (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                    <div>
                                        <div className="fw-bold text-gray-800">
                                            <span className="badge badge-light-warning me-2">Offboarded</span>
                                            This organization is offboarded
                                        </div>
                                        <div className="text-muted fs-7 mt-1">
                                            It is hidden from the leads list and receives no leads — automatic or manual.
                                            Reinstating makes it visible again; automatic syncing stays off until you re-enable it.
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-light-success flex-shrink-0"
                                        onClick={() => applyOffboarded(false)}
                                        disabled={updatingOffboard}
                                    >
                                        {updatingOffboard ? (
                                            <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Reinstating...</>
                                        ) : (
                                            <><i className="bi bi-arrow-counterclockwise me-2"></i>Reinstate Organization</>
                                        )}
                                    </button>
                                </div>
                            ) : (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                                    <div>
                                        <div className="fw-bold text-gray-800">Offboard this organization</div>
                                        <div className="text-muted fs-7 mt-1">
                                            Stops all lead pushing to the 360 — both the nightly automatic sync and the
                                            manual "Sync" buttons — and hides the organization from the leads and management
                                            lists. Use this when a funeral home has been offboarded.
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-light-danger flex-shrink-0"
                                        onClick={() => { setOffboardError(null); setShowOffboardConfirm(true) }}
                                        disabled={updatingOffboard}
                                    >
                                        <i className="bi bi-slash-circle me-2"></i>
                                        Deactivate / Offboard
                                    </button>
                                </div>
                            )}
                        </KTCardBody>
                    </KTCard>
                </>
            )}
        </div>

        {/* Offboard confirmation modal */}
        {showOffboardConfirm && (
            <>
                <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
                <div className="modal fade show d-block" tabIndex={-1} style={{ zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content" style={{ zIndex: 1060 }}>
                            <div className="modal-header">
                                <h5 className="modal-title">Offboard organization?</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowOffboardConfirm(false)}
                                    disabled={updatingOffboard}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-3">
                                    <strong>{passareOrganization.organization_name}</strong> will stop receiving leads entirely:
                                </p>
                                <ul className="text-muted">
                                    <li>The nightly automatic sync will skip it</li>
                                    <li>The manual "Sync All" / per-lead "Sync" buttons will no longer push its leads</li>
                                    <li>It will be hidden from the Passare leads list and this management list</li>
                                </ul>
                                <p className="mb-0 text-muted fs-7">
                                    You can reinstate it later: enable "Show offboarded" in the management list, open the
                                    organization, and click Reinstate.
                                </p>
                                {offboardError && (
                                    <div className="alert alert-danger mt-3 mb-0">{offboardError}</div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    onClick={() => setShowOffboardConfirm(false)}
                                    disabled={updatingOffboard}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => applyOffboarded(true)}
                                    disabled={updatingOffboard}
                                >
                                    {updatingOffboard ? (
                                        <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Offboarding...</>
                                    ) : (
                                        'Yes, offboard'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )}
    </PassareOrganizationDetailContext.Provider >

}

export default PassareOrganizationDetail